import { useEffect, useState } from "react";
import { fingerScan } from "careplix-web-algo";
import { sdkURL } from "../utils/api-url-list";
import LoadingScreen from "../components/LoadingScreen";

const scanMessages = [
  "We burn calories while we are asleep because brain activity requires energy",
  "Exercise promotes cardiovascular health, ensuring more blood and oxygen to circulate the body, helping to elevate energy",
  "Always sleep on your back straight since it allows your neck and spine in a neutral position",
  "Alcohol consumption disrupts chemical impulses between brain cells causing impulsive behaviour, slurred speech, impaired memory",
  "Meditation increases nitric oxide generation in the brain helping to lower your blood pressure",
  "Meditation reduces the production of cytokines in several recent studies, thus helping in stress reduction",
  "Laughing is good for your heart. It reduces stress and gives a boost to your immune system",
  "Lack of sleep can lead to heart disease, heart attack, or stroke",
  "Exercise boosts brain performance",
  "Heart pumps about 2,000 gallons of blood every day",
  "Your heart will beat about 115,000 times each day",
].sort(() => Math.random() - 0.5);

const getMessage = (type = "", timeElapsed = 0) => {
  if (type !== "scan") {
    if (timeElapsed <= 5000) return "Cover your back camera with your finger";
    else if (timeElapsed <= 10000) return "During the measurement, please do not speak or move";
    else if (timeElapsed <= 17000) return "Keep your device steady throughout the vital measurement";
    else if (timeElapsed <= 18000) return "We are good to start in 3";
    else if (timeElapsed <= 19000) return "We are good to start in 2";
    else if (timeElapsed <= 20000) return "We are good to start in 1";
  } else {
    if (timeElapsed <= 30000) return "Scan in Progress...";
    else if (timeElapsed <= 40000) return scanMessages[0];
    else if (timeElapsed <= 50000) return scanMessages[1];
    else if (timeElapsed <= 60000) return scanMessages[2];
    else if (timeElapsed <= 70000) return scanMessages[3];
    else if (timeElapsed <= 80000) return scanMessages[4];
    else if (timeElapsed <= 90000) return scanMessages[5];
    else if (timeElapsed <= 100000) return scanMessages[6];
    else if (timeElapsed <= 110000) return scanMessages[7];
    else if (timeElapsed <= 120000) return scanMessages[8];
    else if (timeElapsed <= 130000) return scanMessages[9];
    else if (timeElapsed <= 140000) return scanMessages[10];
  }
};

const FingerScan = () => {
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState("");
  const [scanFrameData, setScanFrameData] = useState({
    type: "",
    timeElapsed: 0,
    confidence: 0,
    fps: 0,
  });

  useEffect(() => {
    const params = Object.fromEntries(new URLSearchParams(document.location.search).entries());
    if (params?.scan_token?.length > 0) {
      fingerScan.onFrame((fd) => {
        setScanFrameData(fd);
        window?.ReactNativeWebView?.postMessage?.(`framedata:::${JSON.stringify(fd)}`);
      });
      fingerScan.onScanFinish(async ({ raw_intensity, ppg_time, average_fps }) => {
        try {
          setAnalyzing(true);
          const resp = await fetch(sdkURL("/vitals/add-scan"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              api_key: params.api_key,
              scan_token: params.scan_token,
              employee_id: params.employee_id,
              posture: params.posture,
              dob: params.dob,
              gender: params.gender,
              metadata: {
                physiological_scores: { height: params.height, weight: params.weight },
                ppg_time: ppg_time,
                raw_intensity: raw_intensity,
                device: params.device,
                fps: average_fps,
              },
            }),
          });
          const resp_json = await resp.json();
          if (resp_json.statusCode?.toString().startsWith("2")) {
            window?.ReactNativeWebView?.postMessage?.(`success:::${JSON.stringify(resp_json)}`);
          } else throw new Error(resp_json.message);
        } catch (err) {
          setError(err.message);
        }
      });
      fingerScan.onError((err) => {
        setError(err.message);
      });
      fingerScan
        .startScan(60000, 60000)
        .then(() => console.log("Scan Started"))
        .catch(console.error);
      return () => {
        fingerScan.stopScan(true);
      };
    } else setError("Invalid Scan Token");
    return () => {
      fingerScan.stopScan(true);
    };
  }, []);

  return (
    <div className="relative h-screen bg-white">
      {error.length > 0 ? (
        <div className="absolute inset-0 bg-white px-8 py-12 flex flex-col items-center justify-center text-center">
          <img src={process.env.PUBLIC_URL + "/images/failure.gif"} alt="failure icon" className="w-48 mx-auto" />
          <p className="mt-3 text-primary text-center">
            Scan Failed!
            <br />
            Please try again.
          </p>
          <button
            className="mt-8 px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-medium"
            type="button"
            onClick={() => {
              window?.ReactNativeWebView?.postMessage?.("event:::goBack");
            }}
          >
            Go Back
          </button>
        </div>
      ) : analyzing ? (
        <div className="absolute inset-0 bg-white px-8 py-12 flex flex-col items-center justify-center text-center">
          <img src={process.env.PUBLIC_URL + "/images/analyzing.gif"} alt="analyzing icon" className="w-48 mx-auto" />
          <p className="mt-3 text-primary text-xl">Analyzing Data</p>
          <p className="mt-2 text-primary font-light">
            Hold tight, the measurement
            <br />
            results are on the way
          </p>
        </div>
      ) : (
        <>
          <div className="relative h-full w-full">
            <video
              className="fixed bottom-12 left-8 w-px h-px bg-white/80"
              id="videoInput"
              autoPlay
              muted
              playsInline
            />
            <canvas id="canvasOutput" className="h-full w-full -scale-x-100" />
            <div className="fixed left-0 right-0 top-[30vh]">
              {fingerScan.isFingerInView() ? (
                <img src={process.env.PUBLIC_URL + "/images/heartbeat.gif"} alt="heartbeat" className="w-48 mx-auto" />
              ) : (
                <>
                  <svg className="w-48 mx-auto" viewBox="0 0 300 90">
                    <circle fill="#fff" cx="278.63" cy="55.48" r="13.33" />
                    <polyline
                      fill="none"
                      stroke="#fff"
                      strokeWidth={5}
                      strokeMiterlimit={10}
                      points="7.75,54.04 74.69,54.04 83.58,21.09 96.13,72.86 102.93,54.04 118.61,53.51 130.12,10.11 158.36,80.71 169.86,54.04 189.73,54.04 198.1,23.18 200.71,23.18 212.22,68.68 217.45,54.04 265.57,53.96"
                    />
                  </svg>
                  <p className="mt-2 text-xl text-center text-white drop-shadow">Cannot Detect Finger</p>
                </>
              )}
            </div>
            <div
              className="fixed top-8 right-4 rounded-full px-3 py-1.5 text-xs text-white bg-[#f31e2e]"
              style={{
                backgroundColor:
                  scanFrameData.confidence > 0.5 ? (scanFrameData.confidence > 0.9 ? "#a0dd0c" : "#eea508") : "#f31e2e",
              }}
            >
              Signal Strength:&nbsp;
              <span className="font-semibold">
                {scanFrameData.confidence > 0.5 ? (scanFrameData.confidence > 0.9 ? "Good" : "Moderate") : "Poor"}
              </span>
            </div>
            <div className="fixed bottom-10 left-6 right-6 p-6 rounded-xl bg-white/80 flex flex-col">
              <h3 className="text-primary font-semibold">
                {scanFrameData.type === "scan"
                  ? `${Math.round(((scanFrameData.timeElapsed - 20000) / 60000) * 100)}% Completed...`
                  : `Calibration in progress...`}
              </h3>
              <h4 className="mt-2 text-primary text-xs">{getMessage(scanFrameData.type, scanFrameData.timeElapsed)}</h4>
              <button
                type="button"
                onClick={() => {
                  fingerScan.stopScan(true);
                  window?.ReactNativeWebView?.postMessage?.("event:::goBack");
                }}
                className="self-end mt-4 text-[#ED1749] text-sm font-medium"
              >
                &lt;&nbsp;Cancel Scan
              </button>
            </div>
          </div>
          {fingerScan.isInitializing() && (
            <div className="absolute top-0 bottom-0 left-0 right-0 bg-white px-8 py-12 flex flex-col items-center justify-center text-center">
              <LoadingScreen />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FingerScan;
